import React, { createContext, useContext, useCallback } from 'react';
import type { BackendUser, LoginPayload, RegisterPayload, AuthContextType } from '../types/auth.types';
import { amplifyAuthService } from '@services/AmplifyAuthService';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { signInSuccess, signOutSuccess, setLoading, setVerificationStatus } from '../store/slices/authSlice';
import { setUser, clearUser, setUserVerificationStatus } from '../store/slices/userSlice';
import { persistor } from '../store';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const { signedIn, isLoading, token } = useAppSelector((state) => state.auth);
  const user = useAppSelector((state) => state.user);

  const checkAuthStatus = useCallback(async () => {
    return;
  }, []);

  const login = useCallback(async (payload: LoginPayload) => {
    try {
      dispatch(setLoading(true));
      const data = await amplifyAuthService.login(payload);
      const { token, user: userData, id_token } = data;

      dispatch(signInSuccess({
        token,
        idToken: id_token || null
      }));
      dispatch(setUser(userData));
      dispatch(setVerificationStatus(userData.is_email_verified || false));
      dispatch(setUserVerificationStatus(userData.is_email_verified || false));
    } catch (error) {
      dispatch(setLoading(false));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const register = useCallback(async (payload: RegisterPayload) => {
    try {
      dispatch(setLoading(true));
      const data = await amplifyAuthService.register(payload);
      const { token, user: userData, id_token } = data;

      dispatch(signInSuccess({
        token,
        idToken: id_token || null
      }));
      dispatch(setUser(userData));
      dispatch(setVerificationStatus(userData.is_email_verified || false));
      dispatch(setUserVerificationStatus(userData.is_email_verified || false));

      return { success: true, message: 'Registration successful' };
    } catch (error) {
      dispatch(setLoading(false));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const logout = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      await amplifyAuthService.logout();
      // Ensure local state is cleared immediately
      dispatch(signOutSuccess());
      dispatch(clearUser());
      await persistor.purge();
      window.location.href = '/';
    } catch (error) {
      console.error('Sign out error:', error);
      // Even if Amplify sign out fails, clear local state and navigate
      dispatch(signOutSuccess());
      dispatch(clearUser());
      await persistor.purge();
      window.location.href = '/';
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const refreshUserData = useCallback(async () => {
    if (!token) {
      throw new Error('No authentication token');
    }

    try {
      const userData = await amplifyAuthService.getProfile();
      dispatch(setUser(userData));
      dispatch(setVerificationStatus(userData.is_email_verified || false));
      dispatch(setUserVerificationStatus(userData.is_email_verified || false));
    } catch {
      await logout();
    }
  }, [token, logout, dispatch]);

  const updateUserData = useCallback((userData: BackendUser) => {
    dispatch(setUser(userData));
    dispatch(setVerificationStatus(userData.is_email_verified || false));
    dispatch(setUserVerificationStatus(userData.is_email_verified || false));
  }, [dispatch]);

  const handlePasswordReset = useCallback(async () => {
    await refreshUserData();
  }, [refreshUserData]);

  const setAuthFromOAuth = useCallback((token: string, userData: BackendUser) => {
    dispatch(signInSuccess({
      token,
      idToken: token || null
    }));
    dispatch(setUser(userData));
    dispatch(setVerificationStatus(userData.is_email_verified || false));
    dispatch(setUserVerificationStatus(userData.is_email_verified || false));
  }, [dispatch]);

  const sendEmailVerification = useCallback(async (email?: string) => {
    await amplifyAuthService.sendEmailVerification(email || user.email || '');
    return { success: true, message: 'Verification email sent' };
  }, [user.email]);

  const verifyEmail = useCallback(async (email: string, code: string) => {
    await amplifyAuthService.verifyEmail(email, code);
    dispatch(setVerificationStatus(true));
    dispatch(setUserVerificationStatus(true));
    return { success: true, message: 'Email verified successfully' };
  }, [dispatch]);

  const requestPasswordReset = useCallback(async (email: string) => {
    await amplifyAuthService.requestPasswordReset(email);
    return { success: true, message: 'Password reset code sent' };
  }, []);

  const resetPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    await amplifyAuthService.resetPassword(email, code, newPassword);
    return { success: true, message: 'Password reset successful' };
  }, []);

  const setNewPassword = useCallback(async (email: string, temporaryPassword: string, _newPassword: string) => {
    await amplifyAuthService.login({ username: email, password: temporaryPassword });
    return { success: true, message: 'Password updated successfully' };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    await amplifyAuthService.signInWithGoogle();
  }, []);

  const handleOAuthCallback = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      const authResponse = await amplifyAuthService.handleOAuthCallback();

      if (authResponse && authResponse.token && authResponse.user) {
        dispatch(signInSuccess({
          token: authResponse.token,
          idToken: authResponse.id_token || null
        }));
        dispatch(setUser(authResponse.user));
        dispatch(setVerificationStatus(authResponse.user.is_email_verified || false));
        dispatch(setUserVerificationStatus(authResponse.user.is_email_verified || false));

        return {
          success: true,
          message: 'OAuth authentication successful',
          user: authResponse.user
        };
      } else {
        throw new Error('OAuth callback failed - no token or user data received');
      }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'OAuth callback failed'
      };
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const value: AuthContextType = {
    isAuthenticated: signedIn,
    isLoading,
    userToken: token,
    user: user.id ? (user as BackendUser) : null,
    checkAuthStatus,
    login,
    register,
    logout,
    refreshUserData,
    updateUserData,
    handlePasswordReset,
    setAuthFromOAuth,
    sendEmailVerification,
    verifyEmail,
    requestPasswordReset,
    resetPassword,
    setNewPassword,
    signInWithGoogle,
    handleOAuthCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
